import { Injectable } from "@angular/core";
import { CameraOptions } from "@ionic-native/camera";
import { Platform } from 'ionic-angular';
import moment from "moment";
import { Observable } from "rxjs";
import { CameraNative } from "../../../native/camera.native";
import { ActivityRecordingProvider } from "../../../providers/activity-recording/activity-recording";
import { AnalyticsProvider } from "../../../providers/analytics/analytics";
import { FileUploaderProvider, OptionsUploadWithHttp } from "../../../providers/file-uploader/file-uploader";
import { GlobalServiceProvider } from "../../../providers/global-service/global-service";
import { PermissionCheckProvider } from "../../../providers/permission-check/permission-check";
import { StoragePathProvider } from "../../../providers/storage-path/storage-path";
import { AppPreferences, Preference } from "../../../shared/local/app-preferences.local";
import { ActivityProvider } from "../../../shared/nestjs/activity-recording/activity";
import { TaskFeedbackApi } from "../../../shared/nestjs/gd-all-in-one/services/task-feedback.api";
import { RequestCreateActivityDto, RequestGetActivityDto } from "../../../shared/nestjs/gd-only/dto/activity.dto";
import { ActivityApi } from "../../../shared/nestjs/gd-only/services/activity.api";
import { NewActivityApi } from "../../../shared/nestjs/gd-only/services/new-activity.api";
import { NestResponsesProvider } from "../../../shared/nestjs/nest-responses";
import { File } from "@ionic-native/file";
import { ResponseCameraSetting } from "../../../model/camera.model";
import { EventNameAnalytics } from "../../../helpers/event-name-analytics";
import { AlertServiceProvider } from "../../../providers/alert-service/alert-service";
@Injectable()
export class ActivityRecordingBloc {
	public cameraSetting: ResponseCameraSetting = {
		activityRecording: 'default',
		attendance: 'default'
	};

	constructor(
		private activityApi: ActivityApi,
		private platform: Platform,
		private globalServiceProvider: GlobalServiceProvider,
		private analyticsProvider: AnalyticsProvider,
		private storagePathProvider: StoragePathProvider,
		private fileUploaderProvider: FileUploaderProvider,
		private activityProvider: ActivityProvider,
		private activityRecordingProvider: ActivityRecordingProvider,
		private permissionCheckProvider: PermissionCheckProvider,
		private taskFeedbackProvider: TaskFeedbackApi,
		private newActivityApi: NewActivityApi,
		private nestResponsesProvider: NestResponsesProvider,
		private cameraNative: CameraNative,
		private appPreferences: AppPreferences,
		private file: File,
		private alertService: AlertServiceProvider
	) {

	}

	// Get Activity
	public getActivity(data: RequestGetActivityDto): Observable<any> {
		return this.activityProvider.getActivity(data)
	}

	// Save Activity
	private setActivity(data: RequestCreateActivityDto): Observable<any> {
		return this.activityApi.createActivity(data)
	}

	// Get Employee
	private getSearchEmployee(skip: number, limit: number, search: string): Observable<any> {
		return this.activityProvider.getSearchEmployee(limit, skip, search)
	}

	// Get Task
	private getTask(limit: number, skip: number, status: string = 'received'): Observable<any> {
		return this.taskFeedbackProvider.getTask(limit, skip, true, status)
	}

	// Get Activity Type
	private getActivityType(): Observable<any> {
		return this.newActivityApi.type({ filter: 'activity', limit: 1000 })
	}

	// Get Activity Type Detail
	private getActivityTypeDetail(code: any): Observable<any> {
		return this.newActivityApi.detail({
			activityTypeCode: code
		})
	}

	private async createImageSource(options: any, photoUrl: any, imageBase64: any): Promise<{ options: any, photoUrl: any }> {
		if (!this.platform.is('cordova')) {
			photoUrl = await this.globalServiceProvider.convertBase64toBlob(photoUrl);
		} else {
			const getFilePath = this.globalServiceProvider.getFilePath(photoUrl);
			try {
				photoUrl = await this.globalServiceProvider.convertBase64toBlob(imageBase64);
				options = getFilePath.filename;
			} catch (error) {
				options = getFilePath.filename;
				photoUrl = await this.globalServiceProvider.convertBase64toBlob(imageBase64);
			}
		}

		return {
			options: options,
			photoUrl: photoUrl
		};
	}

	private async uploadPhoto(options: any, photoUrl: any, imageBase64: any): Promise<any> {
		try {
			const path = await this.storagePathProvider.getStorageActivityRecord();
			const source = await this.createImageSource(options, photoUrl, imageBase64);

			// console.log('TCL: source', source);
			// console.log('TCL: options', options);
			// console.log('TCL: photoUrl', photoUrl);
			// console.log('TCL: imageBase64', imageBase64);

			const fileName = options.fileName ? options.fileName : moment().valueOf() + '.jpg';
			const option: OptionsUploadWithHttp = {
				nativeURI: source.photoUrl,
				eventsWebview: source.photoUrl,
				type: 'image',
				options: fileName
			}

			const result = await this.fileUploaderProvider.uploadWithHttp(option, path.urlUpload, true, fileName);
			this.analyticsProvider.trackEvent(EventNameAnalytics.ACTIVITY_RECORDING_UPLOAD_SUCCESS, 'Success Upload Photo', (performance.now() - performance.getEntriesByName('apiStart')[0].startTime));
			return result;
		} catch (error) {
			this.analyticsProvider.trackEvent(EventNameAnalytics.ACTIVITY_RECORDING_UPLOAD_FAILED, JSON.stringify(error), (performance.now() - performance.getEntriesByName('apiStart')[0].startTime));
			throw `${error.error.message || error.statusText} (status code ${error.status})`;
		}
	}

	private async geolocationWeb() {
		return new Promise<geolocationAR>(async (resolve) => {
			let location = {
				latitude: null,
				longitude: null,
				mockDetect: false,
				mockValue: [],
				isCancelled: false,
				openSetting: false,
				message: 'Error Getting Location'
			}
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition((position) => {
					if (position) {
						location.latitude = position.coords.latitude;
						location.longitude = position.coords.longitude;
						resolve(location);
					} else {
						resolve(location);
					}
				}, error => {
					resolve(location);
				}, {
					enableHighAccuracy: true,
					timeout: 10000,
					maximumAge: 0
				});
			} else {
				resolve(location);
			}
		})
	}

	private async geolocationAndroid(): Promise<geolocationAR> {
		let location = {
			latitude: null,
			longitude: null,
			mockDetect: false,
			mockValue: [],
			isCancelled: false,
			openSetting: false,
			message: 'Error Getting Location'
		}
		try {
			// check mock location
			const checkMock: any = await this.activityRecordingProvider.checkMockLocation();
			if (checkMock && typeof checkMock.mockDetect != 'undefined') {
				if (checkMock.mockDetect && checkMock.value) {
					location.mockDetect = checkMock.mockDetect;
					location.mockValue = checkMock.value;
					return location;
				}
			}

			// open map
			const data = await this.activityRecordingProvider.getGeolocation();
			if (data.geolocation && data.geolocation.mock) {
				let mock = await this.showAlertMocklocation(data.geolocation.mock);
				if (mock != true) {
					location.isCancelled = true;
					return location;
				}
			}

			location.latitude = data.geolocation.latitude;
			location.longitude = data.geolocation.longitude;
			location.isCancelled = data.cancelled;
			try {
				localStorage.setItem('geolocationCache', JSON.stringify({
					coords: {
						latitude: data.geolocation.latitude,
						longitude: data.geolocation.longitude
					}
				}));
			} catch (error) { }
			return location;
		} catch (error) {
			location.message = error.message;
			return location;
		}
	}

	private async geolocationIOS(): Promise<geolocationAR> {
		let location = {
			latitude: null,
			longitude: null,
			mockDetect: false,
			mockValue: [],
			isCancelled: false,
			openSetting: false,
			message: 'Error Getting Location'
		}
		try {
			const position = await this.permissionCheckProvider.getGeolocation();
			if (position.coords.latitude && position.coords.longitude) {
				location.latitude = position.coords.latitude;
				location.longitude = position.coords.longitude;
				try {
					localStorage.setItem('geolocationCache', JSON.stringify(position));
				} catch (error) { }
			} else {
				const position = JSON.parse(localStorage.getItem('geolocationCache'));
				if (position.coords.latitude && position.coords.longitude) {
					location.latitude = position.coords.latitude;
					location.longitude = position.coords.longitude;
				} else {
					location.message = "Error Getting Location";
				}
			}
			return location;
		} catch (error) {
			if (error.code === 99) {
				location.openSetting = true;
				location.message = error.message;
			} else {
				if (error.message == 'Timeout expired') {
					const position = JSON.parse(localStorage.getItem('geolocationCache'));
					if (position.coords.latitude && position.coords.longitude) {
						location.latitude = position.coords.latitude;
						location.longitude = position.coords.longitude;
					} else {
						location.message = (error.message == 'Timeout expired') ? 'Error Getting Location' : error.message;
					}
				} else {
					location.message = error.message;
				}
			}
			return location;
		}
	}

	async takePicture(): Promise<{ path: any, base64: any, isCancelled: boolean, useOptions: boolean }> {
		const result = {
			path: '',
			base64: '',
			isCancelled: false,
			useOptions: true
		};

		try {
			if (this.platform.is('cordova') && this.cameraSetting.activityRecording !== 'webcam') {
				const options: CameraOptions = {
					sourceType: 1,
					quality: 70,
					allowEdit: false,
					targetWidth: 600,
					targetHeight: 600,
					cameraDirection: 0
				}

				let camera = null

				if (this.cameraSetting.activityRecording === 'camera-device') {
					camera = await this.cameraNative.getCamera(options);
				} else {
					camera = await this.cameraNative.getPicture(options);
				}

				// console.log('TCL: camera', camera)
				if (!camera.cancelled) {
					result.path = camera.nativeURL;
					result.base64 = camera.base64Image;
				} else {
					result.isCancelled = true;
					result.useOptions = false
				}
			}
		} catch (error) {
			console.log(error);
			result.isCancelled = true;
			result.useOptions = false
		}

		return result;
	}

	async getGeolocation(offline: boolean = false): Promise<geolocationAR> {
		if (this.platform.is('cordova')) {
			const isGooglePlayServicesAvailable: boolean = this.globalServiceProvider.getIsGooglePlayServicesAvailable();
			const isGoogleMapsAvailable: boolean = this.globalServiceProvider.getIsGoogleMapsAvailable();
			if (this.platform.is('android') && isGooglePlayServicesAvailable && isGoogleMapsAvailable && !offline) {
				return await this.geolocationAndroid();
			} else {
				const data = await this.geolocationIOS();
				return data
			}
		} else {
			return await this.geolocationWeb();
		}
	}

	// Upload Image
	async uploadImage(images: any[]): Promise<{ fileName: string[], isSuccess: boolean, error?: string }> {
		let fileImages = [];
		try {
			for (let i = 0; i < images.length; i++) {
				const resImage = await this.uploadPhoto(images[i].options, images[i].data, images[i].base64);
				if (resImage) {
					fileImages.push(resImage);
				} else {
					return {
						fileName: fileImages,
						error: 'Photo failed to send',
						isSuccess: false
					};
				}
			}
			return {
				fileName: fileImages,
				isSuccess: true
			};
		} catch (error) {
			return {
				fileName: fileImages,
				error: error,
				isSuccess: false
			};
		}
	}
	// Check Team
	async isExistTeam(): Promise<boolean> {
		try {
			const result = await this.getSearchEmployee(1, 5, '').toPromise();
			return (result.data.length > 1) ? true : false;
		} catch (error) {
			return false;
		}
	}

	// Get Team
	async getTeam(limit: number, skip: number, text: string = ''): Promise<any> {
		try {
			const emp = await this.appPreferences.getAuth();
			const result = await this.getSearchEmployee(skip, limit, text).toPromise();
			result.data = result.data.filter(function (el) {
				return el.empId != emp.empId
			});
			return result
		} catch (error) {
			throw error;
		}
	}

	// Get Data Activity
	async getDataActivity(data: RequestGetActivityDto): Promise<{ data: any, totalPage: number }> {
		try {
			const result = await this.getActivity(data).toPromise();
			return {
				data: result.data,
				totalPage: result.totalPage
			}
		} catch (error) {
			return {
				data: [],
				totalPage: 0
			}
		}
	}

	async getDataTask(limit: number, skip: number): Promise<{ data: any, page: number, totalPage: number }> {
		try {
			const page = skip + 1;
			const result = await this.getTask(limit, page).toPromise();
			return {
				data: result.data,
				page: result.page,
				totalPage: result.totalPage
			};
		} catch (error) {
			return {
				data: [],
				page: 0,
				totalPage: 0
			}
		}
	}

	async getDataActivityType() {
		try {
			const result = await this.getActivityType().toPromise();
			return result.data;
		} catch (error) {
			return [];
		}
	}

	async getDataActivityTypeDetail(code: any) {
		try {
			const result = await this.getActivityTypeDetail(code).toPromise();
			return result.data.schema;
		} catch (error) {
			throw error;
		}
	}

	async saveDataActivity(data: RequestCreateActivityDto) {
		try {
			const result = await this.setActivity(data).toPromise();
			this.analyticsProvider.trackEvent(EventNameAnalytics.ACTIVITY_RECORDING_SUCCESS, result);
			return result;
		} catch (error) {
			this.analyticsProvider.trackEvent(EventNameAnalytics.ACTIVITY_RECORDING_FAILED, error);
			if (typeof error === 'string') {
				throw error;
			} else {
				const message = this.nestResponsesProvider.handleMsgError(error);
				throw message;
			}
		}
	}

	async getCameraSetting() {
		const result: any = await this.appPreferences.getGreatdayStorage(Preference.CameraSetting);
		// console.log('TCL:getCameraSetting', result);

		if (result && result.activityRecording) {
			this.cameraSetting = result || this.cameraSetting;
		}
	}

	async writeFile(file: Blob) {
		const result = { nativeUrl: '' };

		try {
			const auth = await this.appPreferences.getAuth();
			const path = await this.permissionCheckProvider.getPathActivityRecord();
			let date = new Date().getFullYear() + new Date().getMonth() + new Date().getDate();
			let time = new Date().getTime();
			let userId = auth && auth.userId ? auth.userId : 'not_login_yet';
			let fileName = 'img_gd_' + userId + '_' + date + '_' + time + '.jpg';

			const writeFile = await this.file.writeFile(path.nativeURL, fileName, file);
			result.nativeUrl = writeFile.nativeURL;
		} catch (error) {
			console.log('TCL: error write file', error);
		}

		return result;
	}

	showAlertMocklocation(mock: boolean) {
		return new Promise(async (resolve) => {
			if (mock) {
				const message = 'We\'ve detected that there are other apps in the device, which are using Mock Location access (Location Spoofing Apps). Please uninstall first.';
				await this.alertService.createMola(message);
				resolve(false)
			} else {
				resolve(true);
			}
		});
	}
}
export interface geolocationAR {
	latitude: any;
	longitude: any;
	mockDetect: boolean;
	mockValue: any;
	isCancelled: boolean;
	openSetting: boolean;
	message: string;
}



// WEBPACK FOOTER //
// ./src/bloc/feature/activity-recording/activity-recording.bloc.ts